/**
 * GraphData editing shared by raising and lowering — internal to `macros/`
 * (not re-exported from the barrel).
 *
 * `emitMacroInstance` is the ONE place an instance's author-facing content
 * (params, wired params, outputs — exactly what a `macro/…` pseudo-node
 * carries) becomes a tagged expansion. Lowering emits with it; recognition
 * re-emits a stored group with it and compares, which is why recognition
 * needs no rules of its own.
 */

import type { FlowTarget, GraphData, GraphNode, TypeSignature } from '../../../scenedata/behaviors/behaviorgraphdata';
import {
    expandMacro,
    MACRO_PARAM_WIRED,
    type MacroDefinition,
    type MacroExpansion,
    type MacroGraphWriter,
    type MacroShape,
    type MacroWiredValue,
} from './registry';

/**
 * A `MacroGraphWriter` appending to a `GraphData` in place (nodes,
 * declarations, types, events, variables). Appends only — existing indices
 * are never renumbered.
 */
export class GraphDataWriter implements MacroGraphWriter {
    constructor(private readonly _graph: GraphData) {}

    _declarationIndex(op: string): number {
        return declarationIndex(this._graph, op);
    }

    private _typeIndex(signature: TypeSignature): number {
        const existing = this._graph.types.findIndex(t => t.signature === signature);
        if (existing !== -1) return existing;
        this._graph.types.push({ signature });
        return this._graph.types.length - 1;
    }

    _addNode(op: string, config: Record<string, unknown[]>, metadata?: Record<string, unknown>): number {
        const entries = Object.entries(config);
        const configuration: NonNullable<GraphNode['configuration']> = {};
        for (const [key, value] of entries) {
            configuration[key] = { type: this._typeIndex(signatureOf(value[0])), value: [...value] };
        }
        this._graph.nodes.push({
            declaration: this._declarationIndex(op),
            ...(entries.length > 0 ? { configuration } : {}),
            ...(metadata ? { metadata: { ...metadata } } : {}),
        });
        return this._graph.nodes.length - 1;
    }

    _setFlow(fromNode: number, fromSocket: string, toNode: number, toSocket = 'in'): void {
        const node = this._graph.nodes[fromNode];
        (node.flows ??= {})[fromSocket] = { node: toNode, socket: toSocket };
    }

    _setValue(nodeIndex: number, socket: string, value: unknown): void {
        const node = this._graph.nodes[nodeIndex];
        (node.values ??= {})[socket] = { type: this._typeIndex(signatureOf(value)), value: [value] };
    }

    _setValueRef(nodeIndex: number, socket: string, sourceNode: number, sourceSocket = 'value'): void {
        const node = this._graph.nodes[nodeIndex];
        (node.values ??= {})[socket] = { node: sourceNode, socket: sourceSocket };
    }

    _addEvent(): number {
        (this._graph.events ??= []).push({ id: '' });
        return this._graph.events.length - 1;
    }

    _addVariable(name: string, signature: TypeSignature, value: unknown): number {
        this._graph.variables.push({ name, type: this._typeIndex(signature), value: [value] });
        return this._graph.variables.length - 1;
    }
}

/** Narrow a literal to the type signature the graph format records for it. */
function signatureOf(value: unknown): TypeSignature {
    return typeof value === 'boolean' ? 'bool' : typeof value === 'number' ? 'float' : 'string';
}

/** Index of a plain (extension-less) declaration of `op`, appending it when absent. */
export function declarationIndex(graph: GraphData, op: string): number {
    const existing = graph.declarations.findIndex(d => d.op === op && !d.extensions);
    if (existing !== -1) return existing;
    graph.declarations.push({ op });
    return graph.declarations.length - 1;
}

/** The author-facing content of one instance — exactly what its pseudo-node carries. */
export interface MacroInstanceContent {
    /** Inline literal and config params by name. */
    literals: Readonly<Record<string, unknown>>;
    /** Params wired from another node, by name. */
    wired: Readonly<Record<string, MacroWiredValue>>;
    /** Outbound flows by logical output. */
    outputs: Readonly<Record<string, FlowTarget>>;
}

/**
 * Emit one instance's tagged expansion into `graph`: the canonical nodes, its
 * wired params on every target socket, and its outbound flows.
 */
export function emitMacroInstance(
    graph: GraphData,
    def: MacroDefinition,
    content: MacroInstanceContent,
    macroId?: string,
    options: { shape?: MacroShape; silent?: boolean } = {},
): MacroExpansion {
    const shape = options.shape ?? def;
    const writer = new GraphDataWriter(graph);
    const params: Record<string, unknown> = { ...content.literals };
    for (const name of Object.keys(content.wired)) params[name] = MACRO_PARAM_WIRED;
    const expansion = expandMacro(writer, def, params, macroId, options);

    for (const [name, value] of Object.entries(content.wired)) {
        for (const target of shape.params[name]?.targets ?? []) {
            (graph.nodes[expansion.nodes[target.role]].values ??= {})[target.socket] = { ...value };
        }
    }
    for (const [name, flow] of Object.entries(content.outputs)) {
        const ref = shape.outputs[name];
        if (ref) writer._setFlow(expansion.nodes[ref.role], ref.socket, flow.node, flow.socket);
    }
    return expansion;
}

/** Point every node reference (flows, value refs, subgraph interface) through `map`. */
export function remapNodeRefs(graph: GraphData, map: ReadonlyMap<number, number>): void {
    const remap = (index: number): number => map.get(index) ?? index;
    for (const node of graph.nodes) {
        for (const flow of Object.values(node.flows ?? {})) flow.node = remap(flow.node);
        for (const value of Object.values(node.values ?? {})) {
            if ('node' in value) value.node = remap(value.node);
        }
    }
    for (const sockets of [graph.inputFlowSockets, graph.outputFlowSockets]) {
        for (const mapping of Object.values(sockets ?? {})) mapping.node = remap(mapping.node);
    }
    for (const socket of Object.values(graph.inputValueSockets ?? {})) {
        if (socket.node !== undefined) socket.node = remap(socket.node);
    }
    for (const socket of Object.values(graph.outputValueSockets ?? {})) socket.node = remap(socket.node);
}

/** Drop declarations no node references, remapping the rest. */
export function pruneDeclarations(graph: GraphData): void {
    const referenced = new Set(graph.nodes.map(n => n.declaration));
    const map = new Map<number, number>();
    const kept = graph.declarations.filter((_, i) => {
        if (!referenced.has(i)) return false;
        map.set(i, map.size);
        return true;
    });
    if (kept.length === graph.declarations.length) return;
    graph.declarations = kept;
    for (const node of graph.nodes) node.declaration = map.get(node.declaration) ?? node.declaration;
}

/**
 * Remove entries from an indexed table (`events` / `variables`) and re-point
 * the `configKey` configuration of nodes whose op addresses that table.
 */
export function removeTableEntries(
    graph: GraphData,
    table: 'events' | 'variables',
    configKey: string,
    ops: readonly string[],
    removed: ReadonlySet<number>,
): void {
    if (removed.size === 0) return;
    const map = new Map<number, number>();
    const keep = (_: unknown, i: number): boolean => {
        if (removed.has(i)) return false;
        map.set(i, map.size);
        return true;
    };
    if (table === 'events') graph.events = (graph.events ?? []).filter(keep);
    else graph.variables = graph.variables.filter(keep);
    for (const node of graph.nodes) {
        const op = graph.declarations[node.declaration]?.op;
        const config = node.configuration?.[configKey];
        if (!config || !op || !ops.includes(op)) continue;
        const index = config.value[0];
        if (typeof index === 'number') config.value = [map.get(index) ?? index];
    }
}
